const GA_MEASUREMENT_ID = 'G-9YE1NPL9TT';

let isAnalyticsEnabled = false;
let isInitialized = false;
// eslint-disable-next-line no-unused-vars
let isScriptLoading = false;
let initializationPromise = null;
let scriptLoadPromise = null;

// Load GA script
const loadGAScript = () => {
  // If gtag is already available, resolve immediately
  if (typeof window.gtag !== 'undefined') {
    return Promise.resolve();
  }

  // If script is already loading, return the existing promise
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Create new script loading promise
  scriptLoadPromise = new Promise((resolve, reject) => {
    isScriptLoading = true;
    console.log('Loading GA script...');

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;

    const cleanup = () => {
      isScriptLoading = false;
      script.onload = null;
      script.onerror = null;
      scriptLoadPromise = null;
    };

    script.onload = () => {
      console.log('GA script loaded successfully');
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      cleanup();
      resolve();
    };

    script.onerror = error => {
      console.error('Failed to load GA script:', error);
      cleanup();
      reject(new Error('Failed to load GA script'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

// Initialize Google Analytics
export const initGA = async () => {
  const consent = localStorage.getItem('cookieConsent');
  console.log('initGA called, consent:', consent);

  // If already initialized, return immediately
  if (isInitialized) {
    console.log('GA already initialized');
    return;
  }

  // If initialization is in progress, return the existing promise
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start new initialization
  initializationPromise = (async () => {
    try {
      await loadGAScript();

      console.log('Initializing GA...');

      // Double check initialization after script load
      if (isInitialized) {
        console.log('GA was initialized by another call');
        return;
      }

      // Set initial consent state to denied by default
      window.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });

      // Configure GA4 with default settings but don't send any data yet
      window.gtag('config', GA_MEASUREMENT_ID, {
        cookie_domain: window.location.hostname.includes('localhost')
          ? 'localhost'
          : 'devsecopsinc.io',
        cookie_flags: 'SameSite=None;Secure',
        transport_type: 'beacon',
        debug_mode: true,
        send_page_view: false,
        restricted_data_processing: true,
      });

      isInitialized = true;

      // Only enable analytics if consent was previously given
      if (consent === 'accepted') {
        await enableAnalytics();
      }
    } catch (error) {
      console.error('Failed to initialize GA:', error);
      isInitialized = false;
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
};

// Enable analytics
export const enableAnalytics = async () => {
  try {
    if (!isInitialized) {
      await initGA();
    }

    if (!window.gtag) {
      throw new Error('Cannot enable analytics - gtag not found');
    }

    console.log('Enabling analytics:', {
      measurementId: GA_MEASUREMENT_ID,
      hasGtag: !!window.gtag,
      currentState: isAnalyticsEnabled,
      domain: window.location.hostname,
    });

    // Update consent state
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
    });

    // Update configuration to allow data collection
    window.gtag('config', GA_MEASUREMENT_ID, {
      restricted_data_processing: false,
    });

    isAnalyticsEnabled = true;

    // Send initial pageview only after consent is granted
    const initialPath = window.location.pathname + window.location.search;
    console.log('Sending initial pageview after enabling:', initialPath);
    trackPageView(initialPath);
  } catch (error) {
    console.error('Failed to enable analytics:', error);
    isAnalyticsEnabled = false;
  }
};

// Disable analytics
export const disableAnalytics = () => {
  console.log('Disabling GA4');

  if (!window.gtag) {
    console.error('Cannot disable analytics - gtag not found');
    return;
  }

  // Update consent state
  window.gtag('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
  });

  isAnalyticsEnabled = false;
};

// Track page views
export const trackPageView = path => {
  if (!window.gtag) {
    console.error('Google Analytics not loaded - gtag not found');
    return;
  }

  if (!isAnalyticsEnabled) {
    console.log('Analytics is not enabled, page view not tracked:', path);
    return;
  }

  console.log('Tracking page view:', {
    path,
    location: window.location.href,
    title: document.title,
    measurementId: GA_MEASUREMENT_ID,
    isAnalyticsEnabled,
    hasGtag: !!window.gtag,
    domain: window.location.hostname,
  });

  window.gtag('event', 'page_view', {
    send_to: GA_MEASUREMENT_ID,
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    transport_type: 'beacon',
  });
};

// Track events with enhanced GA4 parameters
export const trackEvent = (eventName, category, params = {}) => {
  if (!window.gtag) {
    console.error('Google Analytics not loaded');
    return;
  }

  if (isAnalyticsEnabled) {
    console.log('Tracking event:', eventName, category, params);
    window.gtag('event', eventName, {
      ...params,
      event_category: category,
      send_to: GA_MEASUREMENT_ID,
      transport_type: 'beacon',
    });
  } else {
    console.log('Event not tracked (analytics disabled):', eventName);
  }
};

// Track cookie consent
export const trackCookieConsent = accepted => {
  if (!window.gtag) {
    console.error('Google Analytics not loaded');
    return;
  }

  console.log('Tracking cookie consent:', accepted);

  if (accepted) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }

  if (isAnalyticsEnabled) {
    trackEvent('cookie_consent', 'Consent', {
      consent_status: accepted ? 'accepted' : 'declined',
      page_path: window.location.pathname,
    });
  }
};
